import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createPartnerCredentialsPortalAdmin,
  createPartnerTenantPortalAdmin,
  getMerchantPortalAdminToken,
  listPartnerIntegrationsPortalAdmin,
  listPartnerTenantsForPortalAdmin,
  listPartnerWebhookOutboxPortalAdmin,
  revokePartnerIntegrationPortalAdmin,
  retryPartnerWebhookOutboxPortalAdmin,
} from "../../services/api";
import { friendlyError } from "../../utils/friendlyError";

function partnerDocsHref() {
  const raw = (process.env.REACT_APP_PARTNER_DOCS_URL || "").trim();
  if (!raw) return "";
  return `${raw.replace(/\/+$/, "")}/`;
}

function PartnerDocsLink({ children, style }) {
  const href = partnerDocsHref();
  if (!href) {
    return <span style={style}>{children}</span>;
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={style}>
      {children}
    </a>
  );
}

function formatInstant(v) {
  if (v == null) return "-";
  try {
    const d = typeof v === "string" ? new Date(v) : new Date(Number(v));
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString();
  } catch {
    return String(v);
  }
}

function filenameSlug(str, maxLen = 48) {
  const t = String(str ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  if (!t) return "";
  return t.length > maxLen ? t.slice(0, maxLen).replace(/-+$/, "") : t;
}

function buildCredentialFilenameBase(s) {
  const company = s?.partner_company_name ?? s?.partnerCompanyName ?? "";
  const lab = s?.label ?? "";
  const id = s?.partner_integration_id ?? s?.partnerIntegrationId ?? "new";
  const companyPart = filenameSlug(company) || "company";
  const labelPart = filenameSlug(lab) || "integration";
  let base = `odos-partner-${companyPart}-${labelPart}-${id}`;
  if (base.length > 160) {
    base = base.slice(0, 160).replace(/-+$/, "");
  }
  return base;
}

function pickCredentialFields(s) {
  const apiKey = s?.api_key ?? s?.apiKey ?? "";
  const clientId = s?.client_id ?? s?.clientId ?? "";
  const clientSecret = s?.client_secret ?? s?.clientSecret ?? "";
  const partnerIntegrationId = s?.partner_integration_id ?? s?.partnerIntegrationId ?? "";
  return { apiKey, clientId, clientSecret, partnerIntegrationId };
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function downloadPartnerCredentialsJson(createdSecrets) {
  const { apiKey, clientId, clientSecret, partnerIntegrationId } = pickCredentialFields(createdSecrets);
  const issuedAt = new Date().toISOString();
  const partnerCompany =
    createdSecrets?.partner_company_name ?? createdSecrets?.partnerCompanyName ?? "";
  const integrationLabel = createdSecrets?.label ?? "";
  const merchantId = createdSecrets?.merchant_id ?? createdSecrets?.merchantId ?? "";
  const payload = {
    issued_at: issuedAt,
    note: "ODOS Partner API credentials - store in a secret manager; not shown again by ODOS.",
    partner_company_name: partnerCompany || undefined,
    label: integrationLabel || undefined,
    merchant_id: merchantId === "" ? undefined : merchantId,
    api_key: apiKey,
    client_id: clientId,
    client_secret: clientSecret,
    partner_integration_id: partnerIntegrationId,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
  const stamp = issuedAt.replace(/[:.]/g, "-").slice(0, 19);
  triggerDownload(blob, `${buildCredentialFilenameBase(createdSecrets)}-${stamp}.json`);
}

function downloadPartnerCredentialsEnv(createdSecrets) {
  const { apiKey, clientId, clientSecret, partnerIntegrationId } = pickCredentialFields(createdSecrets);
  const partnerCompany =
    createdSecrets?.partner_company_name ?? createdSecrets?.partnerCompanyName ?? "";
  const integrationLabel = createdSecrets?.label ?? "";
  const merchantId = createdSecrets?.merchant_id ?? createdSecrets?.merchantId ?? "";
  const lines = [
    "# ODOS Partner API - generated " + new Date().toISOString(),
    "# Store securely; not shown again by ODOS.",
    partnerCompany ? `# Partner company: ${partnerCompany}` : null,
    integrationLabel ? `# Integration label: ${integrationLabel}` : null,
    merchantId !== "" ? `# merchant_id (tenant): ${merchantId}` : null,
    "",
    `ODO_PARTNER_API_KEY=${apiKey}`,
    `ODO_PARTNER_CLIENT_ID=${clientId}`,
    `ODO_PARTNER_CLIENT_SECRET=${clientSecret}`,
    `ODO_PARTNER_INTEGRATION_ID=${partnerIntegrationId}`,
    "",
  ].filter((line) => line != null);
  const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  triggerDownload(blob, `${buildCredentialFilenameBase(createdSecrets)}-${stamp}.env.txt`);
}

export default function PartnerIntegrationsAdmin() {
  const navigate = useNavigate();
  const [partnerTenants, setPartnerTenants] = useState([]);
  const [registerPartnerName, setRegisterPartnerName] = useState("");
  const [registeringPartner, setRegisteringPartner] = useState(false);
  const [listMerchantFilter, setListMerchantFilter] = useState("");
  const [integrationSearch, setIntegrationSearch] = useState("");
  const [listPage, setListPage] = useState(0);
  const [integrationsPage, setIntegrationsPage] = useState(null);
  const [integrations, setIntegrations] = useState([]);

  const [createPartnerTenantId, setCreatePartnerTenantId] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [createdSecrets, setCreatedSecrets] = useState(null);

  const [outbox, setOutbox] = useState(null);
  const [outboxStatus, setOutboxStatus] = useState("");
  const [outboxMerchantFilter, setOutboxMerchantFilter] = useState("");
  const [outboxSearch, setOutboxSearch] = useState("");
  const [outboxPage, setOutboxPage] = useState(0);

  const [loading, setLoading] = useState(true);
  const [outboxLoading, setOutboxLoading] = useState(false);
  const [error, setError] = useState("");
  const [outboxError, setOutboxError] = useState("");
  const [creating, setCreating] = useState(false);
  const [partnerCompanySearch, setPartnerCompanySearch] = useState("");

  const filteredPartnerTenants = useMemo(() => {
    const q = partnerCompanySearch.trim().toLowerCase();
    if (!q) return partnerTenants;
    return partnerTenants.filter((m) => {
      const id = String(m.id ?? "");
      const name = String(m.name ?? "").toLowerCase();
      return id.includes(q) || name.includes(q);
    });
  }, [partnerTenants, partnerCompanySearch]);

  const loadPartnerTenants = useCallback(async () => {
    if (!getMerchantPortalAdminToken()) return;
    try {
      const data = await listPartnerTenantsForPortalAdmin();
      setPartnerTenants(Array.isArray(data) ? data : []);
    } catch {
      setPartnerTenants([]);
    }
  }, []);

  const loadIntegrations = useCallback(async () => {
    if (!getMerchantPortalAdminToken()) {
      navigate("/admin/login", { replace: true });
      return;
    }
    setLoading(true);
    setError("");
    try {
      const mid = listMerchantFilter === "" ? undefined : Number(listMerchantFilter);
      const data = await listPartnerIntegrationsPortalAdmin({
        merchantId: Number.isFinite(mid) ? mid : undefined,
        q: integrationSearch.trim() || undefined,
        page: listPage,
        size: 20,
      });
      setIntegrationsPage(data);
      setIntegrations(Array.isArray(data?.content) ? data.content : []);
    } catch (e) {
      if (String(e?.message || "").includes("401") || String(e?.message || "").includes("403")) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(friendlyError(e, "Failed to load partner integrations"));
      setIntegrations([]);
    } finally {
      setLoading(false);
    }
  }, [navigate, listMerchantFilter, listPage, integrationSearch]);

  const loadOutbox = useCallback(async () => {
    if (!getMerchantPortalAdminToken()) return;
    setOutboxLoading(true);
    setOutboxError("");
    try {
      const mid = outboxMerchantFilter === "" ? undefined : Number(outboxMerchantFilter);
      const data = await listPartnerWebhookOutboxPortalAdmin({
        status: outboxStatus || undefined,
        merchantId: Number.isFinite(mid) ? mid : undefined,
        q: outboxSearch.trim() || undefined,
        page: outboxPage,
        size: 15,
      });
      setOutbox(data);
    } catch (e) {
      setOutboxError(friendlyError(e, "Failed to load webhook outbox"));
      setOutbox(null);
    } finally {
      setOutboxLoading(false);
    }
  }, [outboxStatus, outboxMerchantFilter, outboxPage, outboxSearch]);

  useEffect(() => {
    loadPartnerTenants();
  }, [loadPartnerTenants]);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  useEffect(() => {
    loadOutbox();
  }, [loadOutbox]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!newLabel.trim()) return;
    const mid = Number(createPartnerTenantId);
    if (!Number.isFinite(mid)) {
      setError("Select a partner company (register one above if it is not listed yet).");
      return;
    }
    setCreating(true);
    setError("");
    try {
      const res = await createPartnerCredentialsPortalAdmin({
        merchant_id: mid,
        label: newLabel.trim(),
      });
      const tenant = partnerTenants.find((t) => Number(t.id) === mid);
      const companyName = tenant?.name != null ? String(tenant.name).trim() : "";
      setCreatedSecrets({
        ...res,
        partner_company_name: companyName,
      });
      setNewLabel("");
      await loadIntegrations();
    } catch (e) {
      setError(friendlyError(e, "Failed to create integration"));
    } finally {
      setCreating(false);
    }
  }

  async function handleRegisterPartner(e) {
    e.preventDefault();
    const name = registerPartnerName.trim();
    if (!name) return;
    setRegisteringPartner(true);
    setError("");
    try {
      await createPartnerTenantPortalAdmin({ name });
      setRegisterPartnerName("");
      await loadPartnerTenants();
    } catch (err) {
      setError(friendlyError(err, "Failed to register partner company"));
    } finally {
      setRegisteringPartner(false);
    }
  }

  async function handleRevoke(integrationId) {
    if (!window.confirm("Revoke this partner integration? API keys and OAuth for it will stop working.")) return;
    try {
      await revokePartnerIntegrationPortalAdmin(integrationId);
      await loadIntegrations();
    } catch (e) {
      setError(friendlyError(e, "Revoke failed"));
    }
  }

  async function handleRetry(outboxId) {
    try {
      await retryPartnerWebhookOutboxPortalAdmin(outboxId);
      await loadOutbox();
    } catch (e) {
      setOutboxError(friendlyError(e, "Retry failed"));
    }
  }

  const outboxRows = outbox?.content ?? [];
  const outboxTotalPages = outbox?.totalPages ?? 0;
  const intTotalPages = integrationsPage?.totalPages ?? 0;

  return (
    <div className="grid">
      <div className="card">
        <h2 style={{ marginTop: 0 }}>External partner companies</h2>
        <p style={{ marginTop: 0, color: "var(--text-secondary)", maxWidth: 800 }}>
          Same <strong>KYC admin</strong> login as the driver KYC portal. Register each <strong>external</strong> company (they run
          their own storefront; ODOS handles logistics via the Partner API). Then issue API keys per company, revoke access, and
          inspect webhooks. Webhook URLs are set by the partner using{" "}
          <code style={{ color: "var(--accent-muted)" }}>PATCH /api/v1/partner/integration/webhook</code> with the issued API key.{" "}
          Public developer guide for your integration partners:{" "}
          <PartnerDocsLink style={{ color: "var(--accent-muted)" }}>Partner API documentation</PartnerDocsLink>
          {partnerDocsHref() ? "." : " (set REACT_APP_PARTNER_DOCS_URL on this portal build to link to your hosted docs)."}
        </p>

        <details
          style={{
            marginTop: 12,
            maxWidth: 900,
            padding: "12px 14px",
            borderRadius: 14,
            border: "1px solid var(--border-color)",
            background: "var(--primary-light)",
            color: "var(--text-secondary)",
            fontSize: 14,
          }}
        >
          <summary style={{ cursor: "pointer", color: "var(--text-primary)", fontWeight: 600 }}>
            How partners configure outbound webhooks
          </summary>
          <p style={{ marginTop: 12, marginBottom: 8 }}>
            Partners call ODOS as themselves using the issued <strong>API key</strong> (<code>X-ODOS-Partner-Key</code>) or an{" "}
            <strong>OAuth access token</strong> from <code>POST /api/v1/partner/oauth/token</code>. The authenticated principal
            is always one <strong>partner integration</strong> row, so no integration id is passed in the URL.
          </p>
          <pre
            style={{
              margin: 0,
              padding: 12,
              borderRadius: 10,
              overflowX: "auto",
              fontSize: 12,
              background: "var(--primary-darker)",
              color: "var(--accent-muted)",
            }}
          >
            {`PATCH /api/v1/partner/integration/webhook
Authorization: Bearer <access_token>   # or X-ODOS-Partner-Key: <api_key>
Content-Type: application/json

{
  "webhook_url": "https://partner.example.com/odos/webhook",
  "regenerate_secret": false
}`}
          </pre>
          <p style={{ marginTop: 10, marginBottom: 0 }}>
            Setting a non-empty <code>webhook_url</code> stores the callback URL. If there is no secret yet, the server mints one
            and returns <code>webhook_secret</code> once. Set <code>regenerate_secret</code> to <code>true</code> in the JSON
            body to rotate the secret (previous signatures may still verify during the rotation grace window). Share the public{" "}
            <PartnerDocsLink style={{ color: "var(--accent-muted)" }}>partner developer guide</PartnerDocsLink>{" "}
            with partners; full signature material comes from your ODOS integration contact.
          </p>
        </details>

        <div style={{ marginTop: 16, maxWidth: 420 }}>
          <label htmlFor="adm-partner-filter" style={{ display: "block", fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>
            Search partner companies (dropdowns below)
          </label>
          <input
            id="adm-partner-filter"
            type="search"
            value={partnerCompanySearch}
            onChange={(ev) => setPartnerCompanySearch(ev.target.value)}
            placeholder="Company name or id"
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 14,
              border: "1px solid var(--border-color)",
              background: "var(--primary-light)",
              color: "var(--text-primary)",
            }}
          />
        </div>

        {error ? (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 14,
              background: "var(--error-bg)",
              border: "1px solid var(--error-border)",
              color: "var(--error-text)",
            }}
          >
            {error}
          </div>
        ) : null}

        <section style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: "1.05rem", marginBottom: 12 }}>Register partner company</h3>
          <p style={{ marginTop: 0, marginBottom: 12, fontSize: 13, color: "var(--text-secondary)" }}>
            Creates a dedicated ODOS record for that external business (not a marketplace seller). You can then issue API keys
            below.
          </p>
          <form onSubmit={handleRegisterPartner} className="row wrap" style={{ gap: 10, alignItems: "flex-end" }}>
            <div style={{ flex: "1 1 240px" }}>
              <label htmlFor="adm-partner-name" style={{ display: "block", fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>
                Company name
              </label>
              <input
                id="adm-partner-name"
                value={registerPartnerName}
                onChange={(ev) => setRegisterPartnerName(ev.target.value)}
                placeholder="Acme external ecommerce"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 14,
                  border: "1px solid var(--border-color)",
                  background: "var(--primary-light)",
                  color: "var(--text-primary)",
                }}
              />
            </div>
            <button
              type="submit"
              disabled={registeringPartner || !registerPartnerName.trim()}
              style={{
                padding: "10px 20px",
                borderRadius: 14,
                border: "none",
                background: registeringPartner ? "#a59eae" : "var(--accent-primary)",
                color: "#ffffff",
                cursor: registeringPartner || !registerPartnerName.trim() ? "not-allowed" : "pointer",
                fontWeight: 600,
              }}
            >
              {registeringPartner ? "Saving…" : "Add partner company"}
            </button>
          </form>
        </section>

        <section style={{ marginTop: 28 }}>
          <h3 style={{ fontSize: "1.05rem", marginBottom: 12 }}>New API integration</h3>
          <form onSubmit={handleCreate} className="row wrap" style={{ gap: 10, alignItems: "flex-end" }}>
            <div style={{ flex: "1 1 200px" }}>
              <label htmlFor="adm-merchant" style={{ display: "block", fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>
                Partner company
              </label>
              <select
                id="adm-merchant"
                value={createPartnerTenantId}
                onChange={(ev) => setCreatePartnerTenantId(ev.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 14,
                  border: "1px solid var(--border-color)",
                  background: "var(--primary-light)",
                  color: "var(--text-primary)",
                }}
              >
                <option value="">- Select -</option>
                {filteredPartnerTenants.map((m) => (
                  <option key={m.id} value={m.id}>
                    #{m.id} {m.name || ""}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: "1 1 200px" }}>
              <label htmlFor="adm-label" style={{ display: "block", fontSize: 12, color: "var(--text-secondary)", marginBottom: 4 }}>
                Integration label
              </label>
              <input
                id="adm-label"
                value={newLabel}
                onChange={(ev) => setNewLabel(ev.target.value)}
                placeholder="Acme ecommerce - production"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 14,
                  border: "1px solid var(--border-color)",
                  background: "var(--primary-light)",
                  color: "var(--text-primary)",
                }}
              />
            </div>
            <button
              type="submit"
              disabled={creating || !newLabel.trim() || !createPartnerTenantId}
              style={{
                padding: "10px 20px",
                borderRadius: 14,
                border: "none",
                background: creating ? "#a59eae" : "var(--accent-primary)",
                color: "#ffffff",
                cursor: creating || !newLabel.trim() || !createPartnerTenantId ? "not-allowed" : "pointer",
                fontWeight: 600,
              }}
            >
              {creating ? "Creating…" : "Issue API key + OAuth client"}
            </button>
          </form>
        </section>

        <section style={{ marginTop: 28 }}>
          <div className="row wrap" style={{ gap: 12, alignItems: "flex-end", marginBottom: 12 }}>
            <h3 style={{ fontSize: "1.05rem", margin: 0, flex: "1 1 100%" }}>All partner API integrations</h3>
            <label style={{ fontSize: 13, color: "var(--text-secondary)", flex: "1 1 200px" }}>
              Search integrations
              <input
                type="search"
                value={integrationSearch}
                onChange={(ev) => {
                  setListPage(0);
                  setIntegrationSearch(ev.target.value);
                }}
                placeholder="Label or company name"
                style={{
                  display: "block",
                  marginTop: 4,
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 14,
                  border: "1px solid var(--border-color)",
                  background: "var(--primary-light)",
                  color: "var(--text-primary)",
                  minWidth: 160,
                }}
              />
            </label>
            <label style={{ fontSize: 13, color: "var(--text-secondary)" }}>
              Filter by company
              <select
                value={listMerchantFilter}
                onChange={(ev) => {
                  setListPage(0);
                  setListMerchantFilter(ev.target.value);
                }}
                style={{
                  display: "block",
                  marginTop: 4,
                  padding: "8px 10px",
                  borderRadius: 14,
                  border: "1px solid var(--border-color)",
                  background: "var(--primary-light)",
                  color: "var(--text-primary)",
                  minWidth: 200,
                }}
              >
                <option value="">All partner companies</option>
                {filteredPartnerTenants.map((m) => (
                  <option key={m.id} value={String(m.id)}>
                    #{m.id} {m.name || ""}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {loading ? (
            <p style={{ color: "var(--text-secondary)" }}>Loading…</p>
          ) : integrations.length === 0 ? (
            <p style={{ color: "var(--text-secondary)" }}>No integrations.</p>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--border-color)", textAlign: "left", color: "var(--text-secondary)" }}>
                      <th style={{ padding: "8px 6px" }}>ID</th>
                      <th style={{ padding: "8px 6px" }}>Partner company</th>
                      <th style={{ padding: "8px 6px" }}>Integration</th>
                      <th style={{ padding: "8px 6px" }}>Created</th>
                      <th style={{ padding: "8px 6px" }}>Status</th>
                      <th style={{ padding: "8px 6px" }}>OAuth</th>
                      <th style={{ padding: "8px 6px" }}>Webhook</th>
                      <th style={{ padding: "8px 6px" }} />
                    </tr>
                  </thead>
                  <tbody>
                    {integrations.map((row) => {
                      const rev = row.revoked_at ?? row.revokedAt;
                      return (
                        <tr key={row.id} style={{ borderBottom: "1px solid var(--border-light)" }}>
                          <td style={{ padding: "10px 6px", fontFamily: "ui-monospace, monospace" }}>{row.id}</td>
                          <td style={{ padding: "10px 6px", fontSize: 13 }}>
                            #{row.merchant_id ?? row.merchantId}{" "}
                            <span style={{ color: "var(--text-secondary)" }}>{row.merchant_name ?? row.merchantName}</span>
                          </td>
                          <td style={{ padding: "10px 6px" }}>{row.label}</td>
                          <td style={{ padding: "10px 6px", color: "var(--text-secondary)" }}>
                            {formatInstant(row.created_at ?? row.createdAt)}
                          </td>
                          <td style={{ padding: "10px 6px" }}>
                            {rev != null ? (
                              <span style={{ color: "var(--accent-red)" }}>Revoked</span>
                            ) : (
                              <span style={{ color: "var(--accent-green)" }}>Active</span>
                            )}
                          </td>
                          <td style={{ padding: "10px 6px" }}>
                            {row.oauth_configured ?? row.oauthConfigured ? "Yes" : "-"}
                          </td>
                          <td style={{ padding: "10px 6px" }}>
                            {row.webhook_configured ?? row.webhookConfigured ? "Set" : "-"}
                          </td>
                          <td style={{ padding: "10px 6px" }}>
                            {rev == null ? (
                              <button
                                type="button"
                                onClick={() => handleRevoke(row.id)}
                                style={{
                                  padding: "6px 10px",
                                  fontSize: 12,
                                  borderRadius: 10,
                                  border: "1px solid var(--error-border)",
                                  background: "transparent",
                                  color: "var(--error-text)",
                                  cursor: "pointer",
                                }}
                              >
                                Revoke
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {intTotalPages > 1 ? (
                <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
                  <button
                    type="button"
                    disabled={listPage <= 0}
                    onClick={() => setListPage((p) => Math.max(0, p - 1))}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 10,
                      border: "1px solid var(--border-color)",
                      background: listPage <= 0 ? "var(--primary-dark)" : "var(--primary-light)",
                      color: "var(--text-secondary)",
                      cursor: listPage <= 0 ? "not-allowed" : "pointer",
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                    Page {(integrationsPage?.number ?? 0) + 1} / {intTotalPages}
                  </span>
                  <button
                    type="button"
                    disabled={listPage >= intTotalPages - 1}
                    onClick={() => setListPage((p) => p + 1)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 10,
                      border: "1px solid var(--border-color)",
                      background: listPage >= intTotalPages - 1 ? "var(--primary-dark)" : "var(--primary-light)",
                      color: "var(--text-secondary)",
                      cursor: listPage >= intTotalPages - 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    Next
                  </button>
                </div>
              ) : null}
            </>
          )}
        </section>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Webhook outbox</h3>
        <p style={{ color: "var(--text-secondary)", fontSize: 14, marginTop: 0 }}>
          Webhooks for external partner integrations only. Optional filter by company; retry after fixing the partner endpoint.
        </p>
        <div className="row wrap" style={{ gap: 10, marginBottom: 16, alignItems: "center" }}>
          <label style={{ fontSize: 13, color: "var(--text-secondary)", flex: "1 1 200px" }}>
            Search outbox
            <input
              type="search"
              value={outboxSearch}
              onChange={(ev) => {
                setOutboxPage(0);
                setOutboxSearch(ev.target.value);
              }}
              placeholder="Company, event, status, error"
              style={{
                display: "block",
                marginTop: 4,
                width: "100%",
                padding: "6px 10px",
                borderRadius: 10,
                border: "1px solid var(--border-color)",
                background: "var(--primary-light)",
                color: "var(--text-primary)",
              }}
            />
          </label>
          <label style={{ fontSize: 13, color: "var(--text-secondary)" }}>
            Partner company
            <select
              value={outboxMerchantFilter}
              onChange={(ev) => {
                setOutboxPage(0);
                setOutboxMerchantFilter(ev.target.value);
              }}
              style={{
                display: "block",
                marginTop: 4,
                padding: "6px 10px",
                borderRadius: 10,
                border: "1px solid var(--border-color)",
                background: "var(--primary-light)",
                color: "var(--text-primary)",
                minWidth: 180,
              }}
            >
              <option value="">All</option>
              {filteredPartnerTenants.map((m) => (
                <option key={m.id} value={String(m.id)}>
                  #{m.id} {m.name || ""}
                </option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 13, color: "var(--text-secondary)" }}>
            Status
            <select
              value={outboxStatus}
              onChange={(ev) => {
                setOutboxPage(0);
                setOutboxStatus(ev.target.value);
              }}
              style={{
                display: "block",
                marginTop: 4,
                marginLeft: 0,
                padding: "6px 10px",
                borderRadius: 10,
                border: "1px solid var(--border-color)",
                background: "var(--primary-light)",
                color: "var(--text-primary)",
              }}
            >
              <option value="">All</option>
              <option value="PENDING">PENDING</option>
              <option value="DELIVERED">DELIVERED</option>
              <option value="DEAD">DEAD</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => loadOutbox()}
            style={{
              padding: "6px 12px",
              borderRadius: 10,
              border: "1px solid var(--border-color)",
              background: "var(--primary-light)",
              color: "var(--text-secondary)",
              cursor: "pointer",
            }}
          >
            Refresh
          </button>
        </div>
        {outboxError ? (
          <div
            style={{
              marginBottom: 12,
              padding: 10,
              borderRadius: 14,
              background: "var(--error-bg)",
              border: "1px solid var(--error-border)",
              color: "var(--error-text)",
              fontSize: 14,
            }}
          >
            {outboxError}
          </div>
        ) : null}
        {outboxLoading ? (
          <p style={{ color: "var(--text-secondary)" }}>Loading outbox…</p>
        ) : outboxRows.length === 0 ? (
          <p style={{ color: "var(--text-secondary)" }}>No rows.</p>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--border-color)", textAlign: "left", color: "var(--text-secondary)" }}>
                    <th style={{ padding: "6px" }}>ID</th>
                    <th style={{ padding: "6px" }}>Partner company</th>
                    <th style={{ padding: "6px" }}>Event</th>
                    <th style={{ padding: "6px" }}>Status</th>
                    <th style={{ padding: "6px" }}>Attempts</th>
                    <th style={{ padding: "6px" }}>Last error</th>
                    <th style={{ padding: "6px" }} />
                  </tr>
                </thead>
                <tbody>
                  {outboxRows.map((r) => (
                    <tr key={r.id} style={{ borderBottom: "1px solid var(--border-light)" }}>
                      <td style={{ padding: "8px 6px", fontFamily: "ui-monospace, monospace" }}>{r.id}</td>
                      <td style={{ padding: "8px 6px", fontSize: 12 }}>
                        #{r.merchant_id ?? r.merchantId}{" "}
                        <span style={{ color: "var(--text-secondary)" }}>{r.merchant_name ?? r.merchantName}</span>
                      </td>
                      <td style={{ padding: "8px 6px" }}>{r.event_type ?? r.eventType}</td>
                      <td style={{ padding: "8px 6px" }}>{r.status}</td>
                      <td style={{ padding: "8px 6px" }}>{r.attempts}</td>
                      <td
                        style={{
                          padding: "8px 6px",
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "var(--text-secondary)",
                        }}
                        title={r.last_error ?? r.lastError ?? ""}
                      >
                        {r.last_error ?? r.lastError ?? "-"}
                      </td>
                      <td style={{ padding: "8px 6px" }}>
                        {r.status !== "DELIVERED" ? (
                          <button
                            type="button"
                            onClick={() => handleRetry(r.id)}
                            style={{
                              padding: "4px 8px",
                              fontSize: 12,
                              borderRadius: 10,
                              border: "1px solid var(--border-color)",
                              background: "var(--primary-light)",
                              color: "var(--text-primary)",
                              cursor: "pointer",
                            }}
                          >
                            Retry
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {outboxTotalPages > 1 ? (
              <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  disabled={outboxPage <= 0}
                  onClick={() => setOutboxPage((p) => Math.max(0, p - 1))}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 10,
                    border: "1px solid var(--border-color)",
                    background: outboxPage <= 0 ? "var(--primary-dark)" : "var(--primary-light)",
                    color: "var(--text-secondary)",
                    cursor: outboxPage <= 0 ? "not-allowed" : "pointer",
                  }}
                >
                  Previous
                </button>
                <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                  Page {(outbox?.number ?? 0) + 1} / {outboxTotalPages}
                </span>
                <button
                  type="button"
                  disabled={outboxPage >= outboxTotalPages - 1}
                  onClick={() => setOutboxPage((p) => p + 1)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 10,
                    border: "1px solid var(--border-color)",
                    background: outboxPage >= outboxTotalPages - 1 ? "var(--primary-dark)" : "var(--primary-light)",
                    color: "var(--text-secondary)",
                    cursor: outboxPage >= outboxTotalPages - 1 ? "not-allowed" : "pointer",
                  }}
                >
                  Next
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>

      {createdSecrets ? (
        <div
          className="card"
          style={{
            border: "2px solid var(--accent-primary)",
            background: "linear-gradient(145deg, #fcfbfe 0%, #ffffff 100%)",
          }}
        >
          <h3 style={{ marginTop: 0, color: "var(--accent-muted)" }}>Credentials created - copy or download</h3>
          <p style={{ color: "var(--text-secondary)", fontSize: 14 }}>
            Secrets cannot be shown again. Download a file for your secret manager, or copy from below.
          </p>
          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 10 }}>
            <button
              type="button"
              onClick={() => downloadPartnerCredentialsJson(createdSecrets)}
              style={{
                padding: "10px 18px",
                borderRadius: 14,
                border: "none",
                background: "var(--accent-primary)",
                color: "#ffffff",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Download JSON
            </button>
            <button
              type="button"
              onClick={() => downloadPartnerCredentialsEnv(createdSecrets)}
              style={{
                padding: "10px 18px",
                borderRadius: 14,
                border: "1px solid var(--border-color)",
                background: "var(--primary-light)",
                color: "var(--text-primary)",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Download .env.txt
            </button>
          </div>
          <dl style={{ fontSize: 13, fontFamily: "ui-monospace, monospace" }}>
            <dt style={{ color: "var(--text-secondary)", marginTop: 8 }}>API key</dt>
            <dd style={{ margin: "4px 0 0", wordBreak: "break-all" }}>{createdSecrets.api_key ?? createdSecrets.apiKey}</dd>
            <dt style={{ color: "var(--text-secondary)", marginTop: 8 }}>client_id</dt>
            <dd style={{ margin: "4px 0 0", wordBreak: "break-all" }}>{createdSecrets.client_id ?? createdSecrets.clientId}</dd>
            <dt style={{ color: "var(--text-secondary)", marginTop: 8 }}>client_secret</dt>
            <dd style={{ margin: "4px 0 0", wordBreak: "break-all" }}>{createdSecrets.client_secret ?? createdSecrets.clientSecret}</dd>
            <dt style={{ color: "var(--text-secondary)", marginTop: 8 }}>partner_integration_id</dt>
            <dd style={{ margin: "4px 0 0" }}>
              {createdSecrets.partner_integration_id ?? createdSecrets.partnerIntegrationId}
            </dd>
          </dl>
          <button
            type="button"
            onClick={() => setCreatedSecrets(null)}
            style={{
              marginTop: 20,
              padding: "10px 18px",
              borderRadius: 14,
              border: "none",
              background: "var(--accent-primary)",
              color: "#ffffff",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            I have stored these safely
          </button>
        </div>
      ) : null}
    </div>
  );
}
